import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SpeciesForm = ({
  formData,
  handleInputChange,
  handleSubmit,
  handleCancel,
  submitting,
  isEditing,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [imagePreview, setImagePreview] = useState(null);
  const [openSections, setOpenSections] = useState({ description: true, image: true });

  // Update preview when file is selected or existing URL is present
  useEffect(() => {
    if (formData.image && formData.image instanceof File) {
      const url = URL.createObjectURL(formData.image);
      setImagePreview(url);
      return () => URL.revokeObjectURL(url);
    } else if (formData.existing_image_url) {
      setImagePreview(formData.existing_image_url);
    } else if (formData.image_url) {
      setImagePreview(formData.image_url);
    } else {
      setImagePreview(null);
    }
  }, [formData.image, formData.existing_image_url, formData.image_url]);

  const toggleSection = (id) =>
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));

  const handleImageUrlBlur = () => {
    if (formData.image_url) setImagePreview(formData.image_url);
  };

  const clearImage = () => {
    setImagePreview(null);
    handleInputChange({ target: { name: 'image_url', value: '' } });
    handleInputChange({ target: { name: 'image', value: null, type: 'file', files: [] } });
    if (isEditing) {
      handleInputChange({ target: { name: 'remove_image', value: true, type: 'checkbox', checked: true } });
      handleInputChange({ target: { name: 'existing_image_url', value: '' } });
    }
  };

  return (
    <div className={`breed-form-view ${isRTL ? 'rtl' : ''}`}>

      {/* ── Page Header ── */}
      <div className="form-header justify-between">
        <div className="header-left">
          <button className="back-btn" onClick={handleCancel}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            {t('cms.back_to_list', 'Back to List')}
          </button>
          <h3>{isEditing ? t('cms.edit_species', 'Edit Species') : t('cms.add_species', 'Add Species')}</h3>
        </div>
        <div className="status-field">
          <div className="active-switch-row">
            <label 
              htmlFor="species_is_active_toggle" 
              className={`switch-label ${!formData.is_active ? 'switch-label--active' : ''}`}
            >
              {t('cms.inactive', 'Inactive')}
            </label>
            <label className="status-toggle" htmlFor="species_is_active_toggle">
              <input
                type="checkbox"
                id="species_is_active_toggle"
                name="is_active"
                checked={!!formData.is_active}
                onChange={handleInputChange}
              />
              <span className="status-toggle-track"></span>
            </label>
            <label 
              htmlFor="species_is_active_toggle" 
              className={`switch-label ${formData.is_active ? 'switch-label--active' : ''}`}
            >
              {t('cms.active', 'Active')}
            </label>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="breed-form-content">

        {/* ── Meta Row 1: Internal name (slug) + Display Name ── */}
        <div className="bfc-meta-row bfc-section">
          <div className="form-group">
            <label>{t('cms.species_name_slug', 'Internal Name (slug)')} <span className="required">*</span></label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="e.g. dog"
            />
          </div>
          <div className="form-group">
            <label>{t('cms.display_name', 'Display Name')}</label>
            <input
              type="text"
              name="display_name"
              value={formData.display_name}
              onChange={handleInputChange}
              placeholder="e.g. Dog"
            />
          </div>
        </div>

        {/* ── Meta Row 2: Bilingual Names ── */}
        <div className="bfc-meta-row bfc-section">
          <div className="form-group bfc-name-group">
            <label>{t('cms.name_en', 'Name (EN)')} <span className="required">*</span></label>
            <input
              type="text"
              name="name_en"
              value={formData.name_en}
              onChange={handleInputChange}
              required
              placeholder="e.g. Dog"
            />
          </div>
          <div className="form-group bfc-name-group" dir="rtl">
            <label>{t('cms.name_ar', 'الاسم (AR)')} <span className="required">*</span></label>
            <input
              type="text"
              name="name_ar"
              value={formData.name_ar}
              onChange={handleInputChange}
              required
              placeholder="مثال: كلب"
            />
          </div>
        </div>

        {/* ── Description Section ── */}
        <div className="bfc-section">
          <button
            type="button"
            className="bfc-section-header"
            onClick={() => toggleSection('description')}
          >
            <span className="bfc-section-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            </span>
            <span className="bfc-section-title">{t('cms.general_description', 'Description')}</span>
            <svg
              className={`bfc-chevron ${openSections.description ? 'open' : ''}`}
              width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          {openSections.description && (
            <div className="bfc-section-body">
              <div className="bfc-lang-grid">
                {/* English */}
                <div className="form-group">
                  <label className="lang-label">
                    <span className="lang-badge en">EN</span>
                    {t('cms.description_en', 'Description')}
                  </label>
                  <textarea
                    name="description_en"
                    value={formData.description_en || ''}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Describe this species..."
                  />
                </div>

                {/* Arabic */}
                <div className="form-group" dir="rtl">
                  <label className="lang-label">
                    <span className="lang-badge ar">AR</span>
                    {t('cms.description_ar', 'الوصف')}
                  </label>
                  <textarea
                    name="description_ar"
                    value={formData.description_ar || ''}
                    onChange={handleInputChange}
                    rows="4"
                    dir="rtl"
                    placeholder="وصف هذا النوع..."
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Appearance Section ── */}
        <div className="bfc-section">
          <div className="bfc-section-header no-collapse">
            <span className="bfc-section-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 2v20"></path>
                <path d="M12 12l8.66-5"></path>
                <path d="M12 12l8.66 5"></path>
                <path d="M12 12l-8.66 5"></path>
                <path d="M12 12l-8.66-5"></path>
              </svg>
            </span>
            <span className="bfc-section-title">{t('cms.appearance', 'Appearance')}</span>
          </div>

          <div className="bfc-section-body">
            <div className="form-group color-picker-group">
              <label>{t('cms.background_color', 'Background Colour')}</label>
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  name="background_color"
                  value={formData.background_color || '#ffffff'}
                  onChange={handleInputChange}
                  className="visual-color-input"
                />
                <div className="color-picker-info">
                  <span className="color-hex-value">{formData.background_color || '#ffffff'}</span>
                  <span className="color-picker-hint">{t('cms.color_picker_hint', 'Click to choose a color for the species background')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Image Section ── */}
        <div className="bfc-section">
          <button type="button" className="bfc-section-header" onClick={() => toggleSection('image')}>
            <span className="bfc-section-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </span>
            <span className="bfc-section-title">{t('cms.image', 'Species Image')}</span>
            <svg
              className={`bfc-chevron ${openSections.image ? 'open' : ''}`}
              width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          {openSections.image && (
            <div className="bfc-section-body">
              <div className="image-section">
                {/* Inputs */}
                <div className="image-inputs">
                  <div className="form-group">
                    <label>{t('cms.image_url', 'Static Image URL')}</label>
                    <input
                      type="text"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleInputChange}
                      onBlur={handleImageUrlBlur}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('cms.upload_image', 'Upload Image')}</label>
                    <div className="file-input-wrapper">
                      <input
                        type="file"
                        name="image"
                        onChange={handleInputChange}
                        accept="image/*"
                        id="upload-species-image"
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="upload-species-image" className="file-label">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        {formData.image ? formData.image.name : t('cms.choose_file', 'Choose file')}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="image-preview-panel">
                  <span className="image-preview-label">{t('cms.image_preview', 'Image Preview')}</span>
                  {imagePreview ? (
                    <div className="image-preview-box">
                      <img src={imagePreview} alt="Preview" className="image-preview-img" onError={() => setImagePreview(null)} />
                      <button
                        type="button"
                        className="image-preview-clear"
                        onClick={clearImage}
                        title="Remove image"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="image-preview-empty">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>{t('cms.no_image', 'No image selected')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer Actions ── */}
        <div className="form-footer">
          <button type="button" className="cancel-btn-large" onClick={handleCancel}>
            {t('cancel')}
          </button>
          <button type="submit" className="submit-btn-large" disabled={submitting}>
            {submitting ? t('saving') : (isEditing ? t('update') : t('create'))}
          </button>
        </div>

      </form>
    </div>
  );
};

export default SpeciesForm;
